import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  AUTH_MODE,
  clearIdentity,
  isInternalAdmin,
  loadIdentity,
  saveIdentity,
  type AuthMode,
  type Identity,
} from './identity';
import * as cognito from './cognito';
import { api } from '../api/client';

interface AuthContextValue {
  identity: Identity | null;
  mode: AuthMode;
  isInternalAdmin: boolean;
  // Dev: pass an Identity to sign in. Prod: call with no args to redirect to Cognito.
  login: (identity?: Identity) => void;
  logout: () => void;
  // Prod bootstrap: true until the stored OIDC session has been checked on mount.
  initializing: boolean;
  // False while we hold a token but do not yet know the caller's app role.
  // A role-gated screen MUST wait for this before deciding to turn someone away.
  roleReady: boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [identity, setIdentity] = useState<Identity | null>(() => loadIdentity());
  // Prod does an async check for a stored OIDC session on mount; dev is instant.
  const [initializing, setInitializing] = useState(AUTH_MODE === 'prod');
  // Dev sends the role in a header, so it is known from the first render. Prod
  // has to ask the backend, and until it answers an internal admin looks exactly
  // like an attorney.
  const [roleReady, setRoleReady] = useState(AUTH_MODE !== 'prod');
  const sessionRevision = useRef(0);
  const signingOut = useRef(false);

  // Mirror an OIDC user into the shared Identity (access token for the API Bearer).
  const applyCognitoUser = useCallback((user: cognito.User | null) => {
    if (user && !user.expired && user.access_token) {
      const userId = user.profile.sub ?? 'cognito';
      // Carry the role we already resolved for THIS subject. It used to be blanked
      // here on every mount and every silent renew, so an internal admin spent the
      // gap until /me answered looking like an attorney — long enough for an
      // admin-guarded screen to redirect them away with `replace`. Rebinding to a
      // different subject drops it, so one person's role can never be shown for
      // another.
      const prior = loadIdentity();
      const next: Identity = {
        userId,
        // Authoritative role still comes from the server (/me below); this is the
        // last known answer, and the API enforces the real one regardless.
        role: prior?.userId === userId ? prior.role : '',
        token: user.access_token,
        email: typeof user.profile.email === 'string' ? user.profile.email : undefined,
      };
      saveIdentity(next);
      // Update readiness with the identity, before an admin route can render.
      setRoleReady(Boolean(next.role));
      setIdentity(next);
      return next;
    }
    clearIdentity();
    setRoleReady(true);
    setIdentity(null);
    return null;
  }, []);

  // The callback and silent renewal both publish userLoaded. Subscribe before
  // reading storage so the mounted provider sees the first successful sign-in
  // (router navigation does not remount it). The SDK owns automatic renewal.
  useEffect(() => {
    if (AUTH_MODE !== 'prod') return;
    let cancelled = false;
    const unsubscribe = cognito.subscribeUserChanges((user) => {
      if (cancelled || signingOut.current) return;
      sessionRevision.current++;
      applyCognitoUser(user);
      setInitializing(false);
    });
    const revision = sessionRevision.current;

    (async () => {
      let user: cognito.User | null = null;
      try {
        user = await cognito.getStoredUser();
      } catch {
        // A failed stored-session read must settle at sign-in, not spin forever.
      }
      // A newer callback, renewal or logout wins over this older storage read.
      if (cancelled || sessionRevision.current !== revision) return;
      applyCognitoUser(user);
      setInitializing(false);
    })();

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [applyCognitoUser]);

  // Prod: the Cognito access token carries no app role, so once we hold a token
  // but no resolved role, ask the backend who we are. This drives the admin-vs-
  // attorney UI. Re-runs when identity changes; early-returns once the role is set.
  //
  // `roleReady` exists because that gap is not cosmetic. Between restoring the
  // session and /me answering, isInternalAdmin is false for an internal admin —
  // and a guard that redirects on it does so with `replace`, so the admin console
  // was permanently unreachable by URL: /admin bounced to the queue before the
  // role ever arrived, and the history entry was gone.
  useEffect(() => {
    if (AUTH_MODE !== 'prod') return;
    const current = identity;
    // Nothing to ask about (signed out), or the answer is already in hand.
    if (!current?.token || current.role) {
      setRoleReady(true);
      return;
    }
    setRoleReady(false);
    let cancelled = false;
    const revision = sessionRevision.current;
    const isCurrent = () => !cancelled && !signingOut.current && sessionRevision.current === revision;
    void (async () => {
      try {
        const who = await api.me();
        if (!isCurrent()) return;
        if (who.role) {
          const withRole: Identity = { ...current, role: who.role };
          saveIdentity(withRole);
          setIdentity(withRole);
          // The identity change re-runs this effect, which flips roleReady.
          return;
        }
      } catch {
        // Non-fatal: leave role empty (attorney view); retried on next token change.
      }
      // Asked and got no usable role — settle, so a guard can act on the answer
      // rather than spinning forever.
      if (isCurrent()) setRoleReady(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [identity]);

  const login = useCallback((next?: Identity) => {
    if (AUTH_MODE === 'prod') {
      // Redirect to the Cognito Hosted UI; the callback route finishes sign-in.
      // Carry where they were headed, so an emailed link to /portal/<id> survives
      // the round trip instead of depositing them on the review queue.
      void cognito.login(window.location.pathname + window.location.search);
      return;
    }
    if (!next) return;
    // Local development has no Cognito token claims, so issue a fresh
    // correlation key at the sign-in action. It is not an authentication
    // credential; the backend accepts it only in the dev/test auth path.
    const signedIn = { ...next, sessionKey: crypto.randomUUID() };
    saveIdentity(signedIn);
    setIdentity(signedIn);
  }, []);

  const logout = useCallback(() => {
    if (AUTH_MODE === 'prod') {
      signingOut.current = true;
      sessionRevision.current++;
      clearIdentity();
      setIdentity(null);
      setRoleReady(true);
      setInitializing(false);
      void cognito.logout();
      return;
    }
    clearIdentity();
    setIdentity(null);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      identity,
      mode: AUTH_MODE,
      isInternalAdmin: isInternalAdmin(identity),
      login,
      logout,
      initializing,
      // Never claim the role is settled while the stored session is still being
      // checked — that check can itself change who we think we are.
      roleReady: roleReady && !initializing,
    }),
    [identity, login, logout, initializing, roleReady],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>');
  return ctx;
}
